import { StrictMode, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import * as Sentry from '@sentry/react';
import {
  useLocation,
  useNavigationType,
  createRoutesFromChildren,
  matchRoutes,
} from 'react-router-dom';
import App from './App';
import './styles/globals.css';

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  integrations: [
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
    Sentry.replayIntegration(),
  ],
  // Performance monitoring — full sampling in dev/staging, 20% in production
  tracesSampleRate: import.meta.env.MODE === 'production' ? 0.2 : 1.0,
  // Session replay for errors
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
  // Send PII data
  sendDefaultPii: true,
  // Environment
  environment: import.meta.env.MODE,
  // Release tracking — set by Vite define from VERCEL_GIT_COMMIT_SHA
  release: `koda-app@${__SENTRY_RELEASE__}`,
  // Forward events to local Spotlight sidecar in development
  spotlight: import.meta.env.DEV,
});

// Global handler for uncaught promise rejections (network errors, async failures, etc.)
window.onunhandledrejection = (event: PromiseRejectionEvent) => {
  Sentry.captureException(event.reason, {
    tags: { handler: 'unhandledrejection' },
  });
};

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <Sentry.ErrorBoundary fallback={<ErrorFallback />}>
      <App />
    </Sentry.ErrorBoundary>
  </StrictMode>
);

function ErrorFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center p-8">
        <h1 className="text-2xl font-bold text-foreground mb-4">Something went wrong</h1>
        <p className="text-muted-foreground mb-4">We've been notified and are working on a fix.</p>
        <button
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:opacity-90"
        >
          Reload page
        </button>
      </div>
    </div>
  );
}
